const chapter = {
  id: 'differentiation',
  slug: 'differentiation',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Differentiation',
        hindi: 'अवकलन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main, often a building block for AOD',
        hindi: 'जेईई मेन में 1-2 प्रश्न, अक्सर एओडी के लिए एक बिल्डिंग ब्लॉक',
  },
  syllabusTags: ['Calculus', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'rules',
      title: {
        english: 'Basic Rules of Differentiation',
        hindi: 'अवकलन के मूल नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Rules',
        hindi: 'नियम',
          },
          statement: {
            english: `$\\bullet\\ (cf)' = c f'$
$\\bullet\\ (f \\pm g)' = f' \\pm g'$
$\\bullet\\ \\textbf{Product:}\\ (fg)' = f'g + fg'$
$\\bullet\\ \\textbf{Quotient:}\\ (f/g)' = (f'g - fg')/g^2$
$\\bullet\\ \\textbf{Chain:}\\ (f \\circ g)'(x) = f'(g(x)) g'(x)$.`,
            hindi: '$\\bullet\\\\\\\\ (cf)\\\' = c f\\\'$ $\\bullet\\\\\\\\ (f \\pm g)\\\' = f\\\' \\pm g\\\'$ $\\bullet\\\\\\\\ \\textbf{Product:}\\\\\\\\ (fg)\\\' = f\\\'g + fg\\\'$ $\\bullet\\\\\\\\ \\textbf{Quotient:}\\\\\\\\ (f/g)\\\' = (f\\\'g - fg\\\')/g^2$ $\\bullet\\\\\\\\ \\textbf{Chain:}\\\\\\\\ (f \\circ g)\\\'(x) = f\\\'(g(x)) g\\\'(x)$.',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'Chain rule extends to multiple compositions: $\\dfrac{d}{dx} f(g(h(x))) = f\'(g(h(x))) \\cdot g\'(h(x)) \\cdot h\'(x)$.',
        hindi: 'श्रृंखला नियम अनेक रचनाओं तक विस्तारित है: $\\dfrac{d}{dx} f(g(h(x))) = f\'(g(h(x))) \\cdot g\'(h(x)) \\cdot h\'(x)$।',
          },
        },
      ],
    },
    {
      id: 'standard-derivatives',
      title: {
        english: 'Standard Derivatives',
        hindi: 'मानक अवकलज',
      },
      blocks: [
        {
          type: 'table',
          headers: ['$f(x)$', '$f\'(x)$'],
          rows: [
            ['$x^n$', '$n x^{n-1}$'],
            ['$e^x$', '$e^x$'],
            ['$a^x$', '$a^x \\ln a$'],
            ['$\\ln x$', '$1/x$'],
            ['$\\log_a x$', '$1/(x \\ln a)$'],
            ['$\\sin x$', '$\\cos x$'],
            ['$\\cos x$', '$-\\sin x$'],
            ['$\\tan x$', '$\\sec^2 x$'],
            ['$\\cot x$', '$-\\csc^2 x$'],
            ['$\\sec x$', '$\\sec x \\tan x$'],
            ['$\\csc x$', '$-\\csc x \\cot x$'],
            ['$\\sin^{-1} x$', '$1/\\sqrt{1 - x^2}$'],
            ['$\\cos^{-1} x$', '$-1/\\sqrt{1 - x^2}$'],
            ['$\\tan^{-1} x$', '$1/(1 + x^2)$'],
            ['$\\cot^{-1} x$', '$-1/(1 + x^2)$'],
            ['$\\sec^{-1} x$', '$1/(|x|\\sqrt{x^2 - 1})$'],
            ['$\\csc^{-1} x$', '$-1/(|x|\\sqrt{x^2 - 1})$'],
            ['$\\sinh x,\\ \\cosh x$', '$\\cosh x,\\ \\sinh x$'],
          ],
        },
      ],
    },
    {
      id: 'implicit',
      title: {
        english: 'Implicit Differentiation',
        hindi: 'अस्पष्ट अवकलन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'When $y$ is defined implicitly by $F(x, y) = 0$, differentiate both sides w.r.t. $x$ treating $y$ as a function of $x$, then solve for $\\dfrac{dy}{dx}$.',
        hindi: 'जब $y$ को $F(x, y) = 0$ द्वारा अंतर्निहित रूप से परिभाषित किया जाता है, तो दोनों पक्षों को अलग करें। $x$ $y$ को $x$ के फ़ंक्शन के रूप में मानें, फिर $\\dfrac{dy}{dx}$ के लिए हल करें।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find $\\dfrac{dy}{dx}$ if $x^2 + y^2 = 25$.',
        hindi: 'यदि $x^2 + y^2 = 25$ है तो $\\dfrac{dy}{dx}$ ज्ञात करें।',
          },
          solution: {
            english: 'Differentiate: $2x + 2y \\dfrac{dy}{dx} = 0 \\Rightarrow \\dfrac{dy}{dx} = -\\dfrac{x}{y}$.',
        hindi: 'अंतर करें: $2x + 2y \\dfrac{dy}{dx} = 0 \\Rightarrow \\dfrac{dy}{dx} = -\\dfrac{x}{y}$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'If $x^y = y^x$, find $\\dfrac{dy}{dx}$.',
        hindi: 'यदि $x^y = y^x$, तो $\\dfrac{dy}{dx}$ खोजें।',
          },
          solution: {
            english: 'Take logs: $y \\ln x = x \\ln y$. Differentiate: $\\dfrac{dy}{dx} \\ln x + \\dfrac{y}{x} = \\ln y + \\dfrac{x}{y} \\dfrac{dy}{dx}$. Solve: $\\dfrac{dy}{dx} = \\dfrac{\\ln y - y/x}{\\ln x - x/y} = \\dfrac{y(x \\ln y - y)}{x(y \\ln x - x)}$.',
        hindi: 'लॉग लें: $y \\ln x = x \\ln y$। अंतर करें: $\\dfrac{dy}{dx} \\ln x + \\dfrac{y}{x} = \\ln y + \\dfrac{x}{y} \\dfrac{dy}{dx}$. हल करें: $\\dfrac{dy}{dx} = \\dfrac{\\ln y - y/x}{\\ln x - x/y} = \\dfrac{y(x \\ln y - y)}{x(y \\ln x - x)}$.',
          },
        },
      ],
    },
    {
      id: 'parametric',
      title: {
        english: 'Parametric Differentiation',
        hindi: 'प्राचलिक अवकलन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Parametric form',
        hindi: 'पैरामीट्रिक फॉर्म',
          },
          statement: {
            english: 'If $x = f(t),\\ y = g(t)$, then $\\dfrac{dy}{dx} = \\dfrac{dy/dt}{dx/dt} = \\dfrac{g\'(t)}{f\'(t)}$ (provided $f\'(t) \\ne 0$).',
        hindi: 'यदि $x = f(t),\\ y = g(t)$, तो $\\dfrac{dy}{dx} = \\dfrac{dy/dt}{dx/dt} = \\dfrac{g\'(t)}{f\'(t)}$ (बशर्ते $f\'(t) \\ne 0$)।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'If $x = a(\\theta - \\sin\\theta),\\ y = a(1 - \\cos\\theta)$ (cycloid), find $\\dfrac{dy}{dx}$.',
        hindi: 'यदि $x = a(\\theta - \\sin\\theta),\\ y = a(1 - \\cos\\theta)$ (चक्रवात), तो $\\dfrac{dy}{dx}$ खोजें।',
          },
          solution: {
            english: '$\\dfrac{dy}{dx} = \\dfrac{a \\sin\\theta}{a(1 - \\cos\\theta)} = \\cot(\\theta/2)$ using half-angle identities.',
        hindi: '$\\dfrac{dy}{dx} = \\dfrac{a \\sin\\theta}{a(1 - \\cos\\theta)} = \\cot(\\theta/2)$ अर्ध-कोण सर्वसमिकाओं का उपयोग करते हुए।',
          },
        },
      ],
    },
    {
      id: 'logarithmic',
      title: {
        english: 'Logarithmic Differentiation',
        hindi: 'लघुगणकीय अवकलन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Useful when (i) the function is a product/quotient of many factors, or (ii) the base **and** exponent both involve $x$ (like $u(x)^{v(x)}$).',
        hindi: 'तब उपयोगी होता है जब (i) फ़ंक्शन कई कारकों का product/quotient होता है, या (ii) आधार **और** घातांक दोनों में $x$ (जैसे $u(x)^{v(x)}$) शामिल होता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Power-of-power rule',
        hindi: 'पावर-ऑफ-पॉवर नियम',
          },
          statement: {
            english: `For $y = u^v$ where $u, v$ are functions of $x$:
$$\\dfrac{dy}{dx} = u^v \\left[v' \\ln u + \\dfrac{v u'}{u}\\right].$$`,
            hindi: '$y = u^v$ के लिए जहां $u, v$ $x$ के कार्य हैं: $$\\dfrac{dy}{dx} = u^v \\left[v\\\' \\ln u + \\dfrac{v u\\\'}{u}\\right].$$',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Differentiate $y = x^x$.',
        hindi: '$y = x^x$ में अंतर करें।',
          },
          solution: {
            english: 'Take logs: $\\ln y = x \\ln x$. Differentiate: $\\dfrac{1}{y} \\dfrac{dy}{dx} = \\ln x + 1 \\Rightarrow \\dfrac{dy}{dx} = x^x (1 + \\ln x)$.',
        hindi: 'लॉग लें: $\\ln y = x \\ln x$। अंतर करें: $\\dfrac{1}{y} \\dfrac{dy}{dx} = \\ln x + 1 \\Rightarrow \\dfrac{dy}{dx} = x^x (1 + \\ln x)$.',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Differentiate $y = (\\sin x)^x + x^{\\sin x}$.',
        hindi: '$y = (\\sin x)^x + x^{\\sin x}$ का अंतर बताएं।',
          },
          solution: {
            english: `For $y_1 = (\\sin x)^x$: $y_1' = (\\sin x)^x [\\ln \\sin x + x \\cot x]$.
For $y_2 = x^{\\sin x}$: $y_2' = x^{\\sin x} [\\cos x \\ln x + \\dfrac{\\sin x}{x}]$.
Add them.`,
            hindi: '$y_1 = (\\sin x)^x$ के लिए: $y_1\\\' = (\\sin x)^x [\\ln \\sin x + x \\cot x]$। $y_2 = x^{\\sin x}$ के लिए: $y_2\\\' = x^{\\sin x} [\\cos x \\ln x + \\dfrac{\\sin x}{x}]$। उनको जोड़ों।',
          },
        },
      ],
    },
    {
      id: 'inverse-trig-tricks',
      title: {
        english: 'Tricks with Inverse Trig Functions',
        hindi: 'प्रतिलोम त्रिकोणमितीय फलनों की युक्तियाँ',
      },
      blocks: [
        {
          type: 'tip',
          text: {
            english: 'Before differentiating an inverse-trig expression, try simplifying using a trigonometric **substitution** like $x = \\tan\\theta$, $x = \\sin\\theta$, etc. The result often becomes linear in $\\theta$, making differentiation trivial.',
        hindi: 'व्युत्क्रम-ट्रिग अभिव्यक्ति को अलग करने से पहले, $x = \\tan\\theta$, $x = \\sin\\theta$, आदि जैसे त्रिकोणमितीय **प्रतिस्थापन** का उपयोग करके सरल बनाने का प्रयास करें। परिणाम अक्सर $\\theta$ में रैखिक हो जाता है, जिससे अवकलन तुच्छ हो जाता है।',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Differentiate $y = \\tan^{-1}\\left(\\dfrac{2x}{1 - x^2}\\right)$ for $|x| < 1$.',
        hindi: '$|x| के लिए $y = \\tan^{-1}\\left(\\dfrac{2x}{1 - x^2}\\right)$ को अलग करें। <1$.',
          },
          solution: {
            english: 'Let $x = \\tan\\theta$ with $\\theta \\in (-\\pi/4, \\pi/4)$. Then expression $= \\tan^{-1}(\\tan 2\\theta) = 2\\theta = 2 \\tan^{-1} x$. Hence $\\dfrac{dy}{dx} = \\dfrac{2}{1 + x^2}$.',
        hindi: 'मान लीजिए $x = \\tan\\theta$ को $\\theta \\in (-\\pi/4, \\pi/4)$ के साथ। फिर अभिव्यक्ति $= \\tan^{-1}(\\tan 2\\theta) = 2\\theta = 2 \\tan^{-1} x$. इसलिए $\\dfrac{dy}{dx} = \\dfrac{2}{1 + x^2}$.',
          },
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Differentiate $y = \\sin^{-1}(2x \\sqrt{1 - x^2})$ for $|x| \\le 1/\\sqrt 2$.',
        hindi: '$|x| के लिए $y = \\sin^{-1}(2x \\sqrt{1 - x^2})$ का अंतर करें। \\le 1/\\sqrt 2$.',
          },
          solution: {
            english: 'Let $x = \\sin\\theta$, so expression $= \\sin^{-1}(\\sin 2\\theta) = 2\\theta = 2 \\sin^{-1} x$. Hence $\\dfrac{dy}{dx} = \\dfrac{2}{\\sqrt{1 - x^2}}$.',
        hindi: 'मान लीजिए $x = \\sin\\theta$, तो व्यंजक $= \\sin^{-1}(\\sin 2\\theta) = 2\\theta = 2 \\sin^{-1} x$। इसलिए $\\dfrac{dy}{dx} = \\dfrac{2}{\\sqrt{1 - x^2}}$.',
          },
        },
        {
          type: 'note',
          text: {
            english: 'Always check the **range** of $x$ — the simplification $\\sin^{-1}(\\sin\\alpha) = \\alpha$ only holds for $\\alpha \\in [-\\pi/2, \\pi/2]$. Outside, signs change.',
        hindi: 'हमेशा $x$ की **सीमा** की जांच करें - सरलीकरण $\\sin^{-1}(\\sin\\alpha) = \\alpha$ केवल $\\alpha \\in [-\\pi/2, \\pi/2]$ के लिए है। बाहर, संकेत बदल जाते हैं।',
          },
        },
      ],
    },
    {
      id: 'higher-order',
      title: {
        english: 'Higher-Order Derivatives',
        hindi: 'उच्च कोटि के अवकलज',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: '$n$-th derivative',
        hindi: '$n$-वां व्युत्पन्न',
          },
          body: {
            english: '$f^{(n)}(x) = \\dfrac{d^n y}{dx^n}$. Notation: $y\', y\'\', y\'\'\', y^{(4)}, \\ldots$',
        hindi: '$f^{(n)}(x) = \\dfrac{d^n y}{dx^n}$. संकेतन: $y\', y\'\', y\'\'\', y^{(4)}, \\ldots$',
          },
        },
        {
          type: 'table',
          headers: ['$f(x)$', '$f^{(n)}(x)$'],
          rows: [
            ['$e^{ax}$', '$a^n e^{ax}$'],
            ['$\\sin(ax + b)$', '$a^n \\sin(ax + b + n\\pi/2)$'],
            ['$\\cos(ax + b)$', '$a^n \\cos(ax + b + n\\pi/2)$'],
            ['$(ax + b)^m$', '$\\dfrac{m!}{(m-n)!} a^n (ax + b)^{m-n}$ for $n \\le m$'],
            ['$\\dfrac{1}{ax + b}$', '$\\dfrac{(-1)^n n! a^n}{(ax + b)^{n+1}}$'],
            ['$\\ln(ax + b)$', '$\\dfrac{(-1)^{n-1}(n-1)! a^n}{(ax + b)^n}$'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Leibniz rule',
        hindi: 'लीबनिज नियम',
          },
          statement: {
            english: `For the $n$-th derivative of a product:
$$(uv)^{(n)} = \\sum_{k=0}^{n} \\binom{n}{k} u^{(n-k)} v^{(k)}.$$`,
            hindi: 'किसी उत्पाद के $n$वें व्युत्पन्न के लिए: $$(uv)^{(n)} = \\sum_{k=0}^{n} \\binom{n}{k} u^{(n-k)} v^{(k)}.$$',
          },
        },
        {
          type: 'example',
          number: 8,
          problem: {
            english: 'Find $\\dfrac{d^n}{dx^n}(x^2 e^x)$.',
        hindi: '$\\dfrac{d^n}{dx^n}(x^2 e^x)$ खोजें।',
          },
          solution: {
            english: 'By Leibniz with $u = e^x,\\ v = x^2$: $(x^2 e^x)^{(n)} = e^x [x^2 + n \\cdot 2x + \\binom{n}{2} \\cdot 2] = e^x [x^2 + 2nx + n(n-1)]$.',
        hindi: 'लीबनिज़ द्वारा $u = e^x,\\ v = x^2$: $(x^2 e^x)^{(n)} = e^x [x^2 + n \\cdot 2x + \\binom{n}{2} \\cdot 2] = e^x [x^2 + 2nx + n(n-1)]$ के साथ।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '(fg)\' = f\'g + fg\'',
      desc: {
        english: 'Product rule',
        hindi: 'उत्पाद नियम',
      },
    },
    {
      latex: '(f/g)\' = (f\'g - fg\')/g^2',
      desc: {
        english: 'Quotient rule',
        hindi: 'भागफल नियम',
      },
    },
    {
      latex: '(f \\circ g)\'(x) = f\'(g(x)) g\'(x)',
      desc: {
        english: 'Chain rule',
        hindi: 'श्रृंखला नियम',
      },
    },
    {
      latex: '\\tfrac{dy}{dx} = \\tfrac{dy/dt}{dx/dt}',
      desc: {
        english: 'Parametric',
        hindi: 'पैरामीट्रिक',
      },
    },
    {
      latex: '(u^v)\' = u^v[v\' \\ln u + v u\'/u]',
      desc: {
        english: 'Power-of-power',
        hindi: 'शक्ति-की-शक्ति',
      },
    },
    {
      latex: '(\\sin^{-1} x)\' = 1/\\sqrt{1-x^2}',
      desc: {
        english: '',
        hindi: '(-1 एक्स)\' = 1/',
      },
    },
    {
      latex: '(\\tan^{-1} x)\' = 1/(1+x^2)',
      desc: {
        english: '',
        hindi: '( -1 x)\' = 1/(1+x 2)',
      },
    },
    {
      latex: '(uv)^{(n)} = \\sum \\binom{n}{k} u^{(n-k)} v^{(k)}',
      desc: {
        english: 'Leibniz rule',
        hindi: 'लीबनिज नियम',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Inverse trig',
      problem: {
        english: 'If $y = \\tan^{-1}\\left(\\dfrac{1 - \\cos x}{\\sin x}\\right)$, find $\\dfrac{dy}{dx}$.',
        hindi: 'यदि $y = \\tan^{-1}\\left(\\dfrac{1 - \\cos x}{\\sin x}\\right)$, तो $\\dfrac{dy}{dx}$ खोजें।',
      },
      answer: {
        english: '$1/2$',
        hindi: '$1/2$',
      },
      solution: {
        english: '$\\dfrac{1 - \\cos x}{\\sin x} = \\tan(x/2)$, so $y = x/2$ and $dy/dx = 1/2$.',
        hindi: '$\\dfrac{1 - \\cos x}{\\sin x} = \\tan(x/2)$, इसलिए $y = x/2$ और $dy/dx = 1/2$।',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Implicit',
      problem: {
        english: 'If $\\sin y = x \\sin(a + y)$, then $\\dfrac{dy}{dx}$ at $x = 0$ is:',
        hindi: 'यदि $\\sin y = x \\sin(a + y)$, तो $\\dfrac{dy}{dx}$ $x = 0$ पर है:',
      },
      answer: {
        english: '$\\sin a$',
        hindi: '$\\sin a$',
      },
      solution: {
        english: 'At $x = 0$: $\\sin y = 0 \\Rightarrow y = 0$. Differentiate implicitly: $\\cos y \\cdot y\' = \\sin(a+y) + x \\cos(a+y) y\'$. At $(0,0)$: $y\' = \\sin a$.',
        hindi: '$x = 0$ पर: $\\sin y = 0 \\Rightarrow y = 0$. स्पष्ट रूप से अंतर करें: $\\cos y \\cdot y\' = \\sin(a+y) + x \\cos(a+y) y\'$। $(0,0)$ पर: $y\' = \\sin a$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Logarithmic',
      problem: {
        english: 'If $y = (\\ln x)^x$, find $\\dfrac{dy}{dx}$.',
        hindi: 'यदि $y = (\\ln x)^x$, तो $\\dfrac{dy}{dx}$ खोजें।',
      },
      answer: {
        english: '$(\\ln x)^x \\left[\\ln(\\ln x) + \\dfrac{1}{\\ln x}\\right]$',
        hindi: '$(\\ln x)^x \\left[\\ln(\\ln x) + \\dfrac{1}{\\ln x}\\right]$',
      },
      solution: {
        english: 'Use power-of-power formula with $u = \\ln x,\\ v = x$.',
        hindi: '$u = \\ln x,\\\\ v = x$ के साथ पावर-ऑफ-पावर फॉर्मूला का उपयोग करें।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Parametric',
      problem: {
        english: 'If $x = a \\cos^3 t,\\ y = a \\sin^3 t$, then $\\dfrac{dy}{dx} = ?$',
        hindi: 'यदि $x = a \\cos^3 t,\\ y = a \\sin^3 t$, तो $\\dfrac{dy}{dx} =?$',
      },
      answer: {
        english: '$-\\tan t$',
        hindi: '$-\\tan t$',
      },
      solution: {
        english: '$dx/dt = -3a \\cos^2 t \\sin t,\\ dy/dt = 3a \\sin^2 t \\cos t$. Ratio: $-\\tan t$.',
        hindi: '$dx/dt = -3a \\cos^2 t \\sin t,\\ dy/dt = 3a \\sin^2 t \\cos t$. अनुपात: $-\\tan t$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Higher-order',
      problem: {
        english: 'If $y = e^x \\sin x$, prove $y\'\' - 2 y\' + 2 y = 0$.',
        hindi: 'यदि $y = e^x \\sin x$, तो $y\'\' - 2 y\' + 2 y = 0$ सिद्ध करें।',
      },
      answer: {
        english: 'Verified.',
        hindi: 'सत्यापित।',
      },
      solution: {
        english: '$y\' = e^x(\\sin x + \\cos x),\\ y\'\' = 2 e^x \\cos x$. Substitute: $2e^x \\cos x - 2e^x(\\sin x + \\cos x) + 2 e^x \\sin x = 0$. ✓',
        hindi: '$y\' = e^x(\\sin x + \\cos x),\\ y\'\' = 2 e^x \\cos x$. स्थानापन्न: $2e^x \\cos x - 2e^x(\\sin x + \\cos x) + 2 e^x \\sin x = 0$. ✓',
      },
    },
  ],
  next: {
    slug: 'applications-of-derivatives',
    title: 'Applications of Derivatives',
  },
};

export default chapter;
